import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Share,
  Visibility,
  Edit,
  Comment,
  PersonAdd,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Compass } from '../types';
import { apiService } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

const SharedCompassesPage: React.FC = () => {
  const [sharedCompasses, setSharedCompasses] = useState<Compass[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadSharedCompasses();
  }, []);

  const loadSharedCompasses = async () => {
    try {
      setLoading(true);
      const compasses = await apiService.getSharedCompasses();
      setSharedCompasses(compasses);
    } catch (err) {
      setError('Failed to load shared compasses');
      console.error('Error loading shared compasses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewCompass = (compassId: string) => {
    navigate(`/compass/view/${compassId}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted':
        return 'success';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatContent = (content: string | undefined, maxLength: number = 100) => {
    if (!content) return 'No content';
    return content.length > maxLength 
      ? `${content.substring(0, maxLength)}...` 
      : content;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Shared with Me
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Share color="action" />
          <Typography variant="body2" color="text.secondary">
            {sharedCompasses.length} compass{sharedCompasses.length !== 1 ? 'es' : ''} shared
          </Typography>
        </Box>
      </Box>

      {sharedCompasses.length === 0 ? (
        <Card>
          <CardContent>
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <PersonAdd sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No shared compasses yet
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                When someone shares their accountability compass with you,
                it will appear here for you to view and add notes.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {sharedCompasses.map((compass) => (
            <Grid item xs={12} md={6} lg={4} key={compass.id}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="h2" gutterBottom>
                      {compass.title || 'Untitled Compass'}
                    </Typography>
                    <Chip 
                      label={compass.status} 
                      color={getStatusColor(compass.status) as any}
                      size="small"
                    />
                  </Box>

                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      What happened:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      {formatContent(compass.what_happened)}
                    </Typography>
                  </Box>

                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      What I did:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      {formatContent(compass.what_did_i_do)}
                    </Typography>
                  </Box>

                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      How it affected others:
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      {formatContent(compass.how_affected_others)}
                    </Typography>
                  </Box>

                  <Box mb={2}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      What I can do better:
                    </Typography>
                    <Typography variant="body2">
                      {formatContent(compass.what_can_i_do_better)}
                    </Typography>
                  </Box>

                  <Box mt={2} pt={2} borderTop={1} borderColor="divider">
                    <Typography variant="caption" color="text.secondary">
                      Created {formatDistanceToNow(new Date(compass.created_at), { addSuffix: true })}
                    </Typography>
                    {compass.updated_at !== compass.created_at && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Updated {formatDistanceToNow(new Date(compass.updated_at), { addSuffix: true })}
                      </Typography>
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={() => handleViewCompass(compass.id)}
                    fullWidth
                  >
                    View Details
                  </Button>
                  
                  <Tooltip title="Add Notes">
                    <IconButton 
                      color="primary"
                      onClick={() => navigate(`/compass/${compass.id}/notes`)}
                    >
                      <Comment />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default SharedCompassesPage;
